import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, useTheme } from '@mui/material'
import React, { useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehype from 'rehype-raw'
import { dateToHumanTimeWithoutUtcOffset } from '@e3dc-react/shell/Libs/date'

import { NotificationModel } from 'src/Libs/notifications/models/notifications.model'
import { getReactMarkdownComponents } from '../config'
import { getTransformedMarkdown } from '../transformers/get-transformed-markdown'

export interface NotificationMessageModalProps {
  onClose: () => void
  place: number
  notificationMessage: NotificationModel
  markNotificationAsRead: (notificationId: number) => Promise<void>
}

/*
 * This component is a modal in which we can read a notification message
 */
const NotificationMessageModal: React.FC<NotificationMessageModalProps> = ({ notificationMessage, onClose, markNotificationAsRead }) => {
  const { t } = useCustomTranslation()
  const theme = useTheme()

  const reactMarkdownComponents = useMemo(() => getReactMarkdownComponents(theme), [theme])

  const modalActions = {
    primary: {
      name: t('notification.action.mark-as-read'),
      onClick: async () => {
        await markNotificationAsRead(notificationMessage.id)
        onClose()
      },
    },
    secondary: { name: t('common.close') },
  }

  const formattedDate = dateToHumanTimeWithoutUtcOffset(notificationMessage.startAt, t('common.format.date-fns-date'))

  return (
    <Dialog title={formattedDate} actions={modalActions} maxWidth="sm" onClose={onClose}>
      <Box sx={{ paddingTop: '20px', minHeight: 200 }}>
        <Box component="h1" sx={{ padding: 0, margin: 0, fontSize: 20 }}>
          {notificationMessage.title}
        </Box>
        <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehype]} components={reactMarkdownComponents}>
          {getTransformedMarkdown(notificationMessage.text)}
        </ReactMarkdown>
      </Box>
    </Dialog>
  )
}

export default NotificationMessageModal
